export const constructFigmaMessage = (
  initial_code: string,
  figma_context: string,
  figmaImages?: string,
  figmaIcons?: string,
) => {
  const figmaPrompt = `
                    <code>
                        ${initial_code}
                    </code>
                    <context>
                      In the code block is the original React + Tailwind code generated automatically
                      and provided is a screenshot of the Figma frame.
                    </context>
                    <goal>
                      Build off of this code to match the design in the image as closely as possible (do not include any underlying backgrounds).
                      Refactor this code to be more readable, maintain the same layout, and follow best practices for React and Tailwind.
                      Use function components, rename classes/components to be more descriptive, ensure it’s accessible (semantic HTML where possible, alt text, etc.), and keep color/typography consistent.
                    </goal>
                    <deliverable>
                      Create a new storyboard to render a single React component snippet that’s well-structured, commented, and ready to use in a production environment.
                    </deliverable>
                    <specific_design_considerations>
                      <typography>
                        <raw_figma_context>
                          Provided is the raw Figma JSON file, including frames, groups, and text. Use this for typography, spacing and image mapping.
                          ${figma_context}
                        </raw_figma_context>
                        <raw_figma_text_data>
                          Consider the raw json data of all text from the original Figma design JSON file.
                          Use this data to ensure that the typography of the component matches the design in the image as closely as possible.
                          Take into account the font, font weight, font size, line height, letter spacing, and text color of different text blocks in the component.
                        </raw_figma_text_data>
                        <fonts>
                          CRITICAL: You MUST implement fonts from Figma data as your FIRST step before any other code generation.
                          CRITICAL: The font implementation MUST match the exact font family and weight from Figma.
                          CRITICAL: Verify all fonts are correctly rendered in the final output, not just present in configuration.
                          You will receive font data in this structure:
                          {
                            fontFamily: "Font Name",
                            fontWeight: "Weight Style",
                            fontURL: "https://fonts.googleapis.com/css2?family=Font+Name&display=swap" or null
                          }
                          Follow these exact steps in order:

                          COLLECT ALL UNIQUE FONTS

                          Extract and deduplicate all font families from the Figma data
                          For each unique family, identify if it has a fontURL (Google Font) or null (custom/default)

                          If the fontURL is null, do not make any changes to the index.css file for this font and in the component do not specify any font classes.

                          UPDATE INDEX.CSS FILE

                          Keep existing Tailwind directives (@tailwind base, components, utilities)
                          CRITICAL: Add Google Font imports immediately before the Tailwind directives:

                          @import url('{fontURL}');
                          @import url('{fontURL2}');
                          /* Add all other font imports here */
                          @tailwind base;
                          @tailwind components;
                          @tailwind utilities;
                          ...

                          CRITICAL: Use the fontURL exactly as it is provided in the context.

                          CONFIGURE TAILWIND

                          ALWAYS modify tailwind.config.js to extend fontFamily
                          CRITICAL: Use proper quotes for font names with spaces
                          Create a kebab-case key for each font family:
            
                          module.exports = {
                            theme: {
                              extend: {
                                fontFamily: {
                                  inter: ["Inter", "sans-serif"],
                                  "font-name": ["Font Name", "sans-serif"],
                                  // Add all other fonts here
                                }
                              }
                            }
                          }

                          CRITICAL: keys in the fontFamily object must be kebab-case

                          APPLY IN COMPONENTS

                          Map Figma font properties to Tailwind classes:

                          fontFamily → font-{kebab-case-name} (ex: font-inter)
                          CRITICAL: do NOT do font-["Font Name"] Always use font-kebab-case-name. Using font-["Font Name"] will break the component. There is ZERO exception to this rule.

                          fontWeight → Appropriate Tailwind weight class
                          IMPORTANT: font weights should be one class lighter than in the design. Example: fontWeight: "Medium" -> font-normal in the code.
                          Apply classes correctly in JSX: className="font-font-name font-medium"

                          CRITICAL: Ensure ALL text elements have proper font classes applied

                          VERIFY IMPLEMENTATION

                          Confirm all Google Font imports are present with correct weights
                          Check that tailwind.config.js includes all font families
                          Ensure every text component has appropriate font classes
                          Verify that the rendered output matches Figma design

                          CRITICAL: Do not skip any of these steps or leave fonts configured but not applied.
                          CRITICAL: Use the exact font family names from Figma, preserving capitalization and spacing.
                        </fonts>
                      </typography>
                      <spacing_and_alignment>
                        <alignment>
                          Ensure that the alignment of elements matches the design in the image as closely as possible. 
                          Consider where elements are justified, centered, left-aligned, right-aligned, etc... and reflect this in the code's tailwind styling.
                        </alignment
                      </spacing_and_alignment>
                      <padding>
                        Ensure that the padding and spacing between elements match the design in the image as closely as possible. 
                      </padding>
                      <sizing>
                        The proportions of the elements should match the design in the image as closely as possible. 
                        While sizing the component, ensure that no content is cut off or overflowing.
                        Ensure that border widths of elements match the design in the image as closely as possible.
                      </sizing>
                      <icons>
                        Use lucide-react icons for any icons in the design.
                        After importing icons, take into account whether the icon is an outline or filled. If it's an outline ensure that the icon weight matches that in the image.
                        After importing icons, ensure to colour the icons to match the colours of the corresponding icons in the image.
                      </icons>
                      <colors>
                        Use the image provided to get color codes identical to the one's in the image. Do not use the colors included in the code right now. Colors should be exact.
                      </colors>
                      <images>
                      Use this mapping of ids and image urls for images insides the design.

                      ${figmaImages}
                      ${figmaIcons}

                      Use these urls to include the images in the component
                      Use the structure and IDs of the raw figma context to map the correct image to the correct node in the component.
                      Ensure these assets are incorporated into the final component.

                      Image Ids do not need to match exactly with an id in the raw figma data. Prioritize using all images urls.

                      IMPORTANT: Only use the image urls provided in the object for both vector icons and images.

                      IMPORTANT: Be sure to distinguish between icons (vectors, exported as png) and images (pngs) when inserting them into the component. 
                      IMPORTANT: When inserting the images in the right positions, icons and images are fetched from the object similar to figma_context using depth first search.

                      CRITICAL: You MUST use all images provided. 
                      CRITICAL: Do not use any placeholder images from Unsplash or similar sites.
                      </images>
                      <no_backgrounds>
                        Do not include any underlying backgrounds in the component. The actual component should be able to be placed on any background (similar to a png).
                      </no_backgrounds>
                    </specific_design_considerations>
                    <instruction>
                      You MUST add this to a new storyboard. 
                      You MUST render this new storyboard. 

                      CRITICAL: Generate the complete code and component as specified, incorporating all elements, features, and requirements detailed in the prompt and the image. Do not ask if further development is needed assume all aspects must be fully implemented in this single response
                      IMPORTANT: Always develop everything seen in the code and in the image. If you must ask if you should continue to develop something, the answer is ALWAYS yes.
                    </instruction>
                    <create_storyboard_change_object>
                      {
                        "storyboardName": "NewStoryboardName",
                        "size": {"width": 300, "height": 200},
                        "change": {"newFullFileContent": "..."}
                      }
                    </create_storyboard_change_object>
                    <create_storyboard_change_object_instructions>
                      1. Whenever you are creating a new component or a standalone UI, strongly consider doing it using this change type. This will actually show the change to the user.
                      2. Note that this will not actually create a new file in the codebase, but will show the user the new component in the canvas. If you want to create a new component then show it the absolute best way to do that is to create a new file in the codebase using the "UI" change type, then creating a storyboard which just instantiates that component.
                      3. Even if you are not creating a new component, consider using this change type alongside a UI edit to showcase the change to the user. Do this by making a change to the codebase and then creating a storyboard that just returns an instance of the component you changed. Make sure to include static props to the component instance so it renders properly. However, do not do this if this change would already be reflected in one of the existing storyboards.
                      4. Try to use global imports (e.g. @/components/MyComponent) instead of relative imports (e.g. ../../components/MyComponent) in the storyboard file to avoid issues with file paths.
                      5. Try to put the new storyboard somewhat close to other storyboards but not overlapping, try to leave a gap of at least 10 pixels between storyboards.
                    </create_storyboard_change_object_instructions>
                    <post_code_generation_instruction>
                      IMPORTANT: Add and render this new component into a storyboard.
                      The user should be able to see the component in the visual editor.
                      Use the create_storyboard_change_object only to create a new storyboard with the code
                    </post_code_generation_instruction>`;

  const figmaMessage = figmaPrompt
    .split("\n")
    .map((line) => "  " + line)
    .join("\n");

  return figmaMessage;
};

export const constructFigmaMessageForClipboard = (
  initial_code: string,
  figma_context: string,
  figmaImages?: string,
  figmaIcons?: string,
) => {
  const figmaPrompt = `
                    <code>
                        ${initial_code}
                    </code>
                    <context>
                      In the code block is the original React + Tailwind code generated automatically
                      and provided is a screenshot of the Figma frame.
                    </context>
                    <goal>
                      Use the raw representation of the Figma design, the code and the image to use as context in any code generation prompted.
                      
                      For building new components:
                      Build off of this code to match the design in the image as closely as possible (do not include any underlying backgrounds).
                      Refactor this code to be more readable, maintain the same layout, and follow best practices for React and Tailwind.
                      Use function components, rename classes/components to be more descriptive, ensure it’s accessible (semantic HTML where possible, alt text, etc.), and keep color/typography consistent.
                    </goal>
                    <deliverable>
                      If prompted to create a new component: Create a new storyboard to render a single React component snippet that’s well-structured, commented, and ready to use in a production environment.
                    </deliverable>
                    <specific_design_considerations>
                      <typography>
                        <raw_figma_context>
                          Provided is the raw Figma JSON file, including frames, groups, and text. Use this for typography, spacing and image mapping.
                          ${figma_context}
                        </raw_figma_context>
                        <raw_figma_text_data>
                          Consider the raw json data of all text from the original Figma design JSON file.
                          Use this data to ensure that the typography of the component matches the design in the image as closely as possible.
                          Take into account the font, font weight, font size, line height, letter spacing, and text color of different text blocks in the component.
                        </raw_figma_text_data>
                        <fonts>
                          CRITICAL: You MUST implement fonts from Figma data as your FIRST step before any other code generation.
                          CRITICAL: The font implementation MUST match the exact font family and weight from Figma.
                          CRITICAL: Verify all fonts are correctly rendered in the final output, not just present in configuration.
                          You will receive font data in this structure:
                          {
                            fontFamily: "Font Name",
                            fontWeight: "Weight Style",
                            fontURL: "https://fonts.googleapis.com/css2?family=Font+Name&display=swap" or null
                          }
                          Follow these exact steps in order:

                          COLLECT ALL UNIQUE FONTS

                          Extract and deduplicate all font families from the Figma data
                          For each unique family, identify if it has a fontURL (Google Font) or null (custom/default)

                          If the fontURL is null, do not make any changes to the index.css file for this font and in the component do not specify any font classes.

                          UPDATE INDEX.CSS FILE

                          Keep existing Tailwind directives (@tailwind base, components, utilities)
                          CRITICAL: Add Google Font imports immediately before the Tailwind directives:

                          @import url('{fontURL}');
                          @import url('{fontURL2}');
                          /* Add all other font imports here */
                          @tailwind base;
                          @tailwind components;
                          @tailwind utilities;
                          ...

                          CRITICAL: Use the fontURL exactly as it is provided in the context.

                          CONFIGURE TAILWIND

                          ALWAYS modify tailwind.config.js to extend fontFamily
                          CRITICAL: Use proper quotes for font names with spaces
                          Create a kebab-case key for each font family:
            
                          module.exports = {
                            theme: {
                              extend: {
                                fontFamily: {
                                  inter: ["Inter", "sans-serif"],
                                  "font-name": ["Font Name", "sans-serif"],
                                  // Add all other fonts here
                                }
                              }
                            }
                          }

                          CRITICAL: keys in the fontFamily object must be kebab-case

                          APPLY IN COMPONENTS

                          Map Figma font properties to Tailwind classes:

                          fontFamily → font-{kebab-case-name} (ex: font-inter)
                          CRITICAL: do NOT do font-["Font Name"] Always use font-kebab-case-name. Using font-["Font Name"] will break the component. There is ZERO exception to this rule.

                          fontWeight → Appropriate Tailwind weight class
                          IMPORTANT: font weights should be one class lighter than in the design. Example: fontWeight: "Medium" -> font-normal in the code.
                          Apply classes correctly in JSX: className="font-font-name font-medium"

                          CRITICAL: Ensure ALL text elements have proper font classes applied

                          VERIFY IMPLEMENTATION

                          Confirm all Google Font imports are present with correct weights
                          Check that tailwind.config.js includes all font families
                          Ensure every text component has appropriate font classes
                          Verify that the rendered output matches Figma design

                          CRITICAL: Do not skip any of these steps or leave fonts configured but not applied.
                          CRITICAL: Use the exact font family names from Figma, preserving capitalization and spacing.
                        </fonts>
                      </typography>
                      <spacing_and_alignment>
                        <alignment>
                          Ensure that the alignment of elements matches the design in the image as closely as possible. 
                          Consider where elements are justified, centered, left-aligned, right-aligned, etc... and reflect this in the code's tailwind styling.
                        </alignment
                      </spacing_and_alignment>
                      <padding>
                        Ensure that the padding and spacing between elements match the design in the image as closely as possible. 
                      </padding>
                      <sizing>
                        The proportions of the elements should match the design in the image as closely as possible. 
                        While sizing the component, ensure that no content is cut off or overflowing.
                        Ensure that border widths of elements match the design in the image as closely as possible.
                      </sizing>
                      <icons>
                        Use lucide-react icons for any icons in the design.
                        After importing icons, take into account whether the icon is an outline or filled. If it's an outline ensure that the icon weight matches that in the image.
                        After importing icons, ensure to colour the icons to match the colours of the corresponding icons in the image.
                      </icons>
                      <colors>
                        Use the image provided to get color codes identical to the one's in the image. Do not use the colors included in the code right now. Colors should be exact.
                      </colors>
                      <images>
                      Use this mapping of ids and image urls for images insides the design.

                      ${figmaImages}
                      ${figmaIcons}

                      Use these urls to include the images in the component
                      Use the structure and IDs of the raw figma context to map the correct image to the correct node in the component.
                      Ensure these assets are incorporated into the final component.

                      Image Ids do not need to match exactly with an id in the raw figma data. Prioritize using all images urls.

                      IMPORTANT: Only use the image urls provided in the object for both vector icons and images.

                      IMPORTANT: Be sure to distinguish between icons (vectors, exported as png) and images (pngs) when inserting them into the component. 
                      IMPORTANT: When inserting the images in the right positions, icons and images are fetched from the object similar to figma_context using depth first search.

                      CRITICAL: You MUST use all images provided. 
                      CRITICAL: Do not use any placeholder images from Unsplash or similar sites.
                      </images>
                      <no_backgrounds>
                        Do not include any underlying backgrounds in the component. The actual component should be able to be placed on any background (similar to a png).
                      </no_backgrounds>
                    </specific_design_considerations>
                    <instruction>
                      You MUST add this to a new storyboard. 
                      You MUST render this new storyboard. 

                      CRITICAL: Generate the complete code and component as specified, incorporating all elements, features, and requirements detailed in the prompt and the image. Do not ask if further development is needed assume all aspects must be fully implemented in this single response
                      IMPORTANT: Always develop everything seen in the code and in the image. If you must ask if you should continue to develop something, the answer is ALWAYS yes.
                    </instruction>
                    <create_storyboard_change_object>
                      {
                        "storyboardName": "NewStoryboardName",
                        "size": {"width": 300, "height": 200},
                        "change": {"newFullFileContent": "..."}
                      }
                    </create_storyboard_change_object>
                    <create_storyboard_change_object_instructions>
                      1. Whenever you are creating a new component or a standalone UI, strongly consider doing it using this change type. This will actually show the change to the user.
                      2. Note that this will not actually create a new file in the codebase, but will show the user the new component in the canvas. If you want to create a new component then show it the absolute best way to do that is to create a new file in the codebase using the "UI" change type, then creating a storyboard which just instantiates that component.
                      3. Even if you are not creating a new component, consider using this change type alongside a UI edit to showcase the change to the user. Do this by making a change to the codebase and then creating a storyboard that just returns an instance of the component you changed. Make sure to include static props to the component instance so it renders properly. However, do not do this if this change would already be reflected in one of the existing storyboards.
                      4. Try to use global imports (e.g. @/components/MyComponent) instead of relative imports (e.g. ../../components/MyComponent) in the storyboard file to avoid issues with file paths.
                      5. Try to put the new storyboard somewhat close to other storyboards but not overlapping, try to leave a gap of at least 10 pixels between storyboards.
                    </create_storyboard_change_object_instructions>
                    <post_code_generation_instruction>
                      IMPORTANT: Add and render this new component into a storyboard.
                      The user should be able to see the component in the visual editor.
                      Use the create_storyboard_change_object only to create a new storyboard with the code
                    </post_code_generation_instruction>`;

  const figmaMessage = figmaPrompt
    .split("\n")
    .map((line) => "  " + line)
    .join("\n");

  return figmaMessage;
};
